import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import Image from 'next/image'

import {
    ArrowRight,
    Baby,
    CalendarRange,
    Check,
    MessagesSquare,
    Phone,
    Plus,
    ReceiptText
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { CtaBand } from '@/components/generals/cta-band'
import { BookingButton, ButtonLink } from '@/components/generals/button-link'
import { SectionHeading } from '@/components/generals/section-heading'
import { HeroPill, PageHero } from '@/components/generals/page-hero'

interface Service {
    id: string
    icon: LucideIcon
    name: string
    audience: string
    description: string
    included: Array<string>
    price: string
    priceNote?: string
    badge?: string
    featured?: boolean
}

interface Faq {
    question: string
    answer: string
}

const services: Array<Service> = [
    {
        id: 'free-consult',
        icon: Phone,
        name: "Free 15-Minute Consult",
        audience: "Anyone thinking about therapy with me",
        description: "A relaxed phone call to talk about what's going on and see if we're a good fit. No pressure, and no prep needed.",
        included: ["15 minutes by phone", "Answers to your questions", "Next steps if it feels right"],
        price: "Free",
        featured: true
    },
    {
        id: 'individual-therapy',
        icon: MessagesSquare,
        name: "Individual Therapy",
        audience: "Women dealing with anxiety, people-pleasing, burnout or life changes",
        description: "Weekly sessions where we talk through what's weighing on you and practice skills you can use right away. Most clients meet weekly at first, then every other week.",
        included: ["50 minutes", "In person or online", "Simple between-session tools"],
        price: "$165",
        priceNote: "per session"
    },
    {
        id: 'anxiety-skills-intensive',
        icon: CalendarRange,
        name: "Anxiety Skills Intensive",
        audience: "Anyone who wants focused, structured help with anxiety",
        description: "Eight weekly sessions built around one goal: learning the tools that quiet anxiety. You'll get a simple workbook to keep.",
        included: ["8 sessions of 50 minutes", "A skills workbook", "A plan for after we finish"],
        price: "$1,240",
        priceNote: "paid in 2 parts",
        badge: "Save $80"
    },
    {
        id: 'support-for-new-moms',
        icon: Baby,
        name: "Support for New Moms",
        audience: "Moms in the first two years after a baby",
        description: "A calm hour to talk about the overwhelm, guilt and “who am I now?” feelings that come with a new baby. Babies are welcome in online sessions.",
        included: ["50 minutes", "In person or online", "Evening times available"],
        price: "$165",
        priceNote: "per session"
    }
]

const faqs: Array<Faq> = [
    {
        question: "How do I know if I need therapy?",
        answer: "You don't need a crisis to start. If you feel stuck, tired or anxious more often than not, that's reason enough."
    },
    {
        question: "Do you take insurance?",
        answer: "I'm out of network, but I can give you a superbill to submit to your insurance. Many plans pay back part of the cost, so check with yours."
    },
    {
        question: "Is online therapy as good as in person?",
        answer: "For most people, yes. Many clients like doing sessions from home, and you can switch between online and in person."
    },
    {
        question: "How long will I be in therapy?",
        answer: "It depends on what you want to work on. Many clients feel a real difference within 8 to 12 sessions."
    },
    {
        question: "Is what I say private?",
        answer: "Yes. What you share stays between us, with a few legal exceptions I'll explain at your first session."
    },
    {
        question: "What's your cancellation policy?",
        answer: "Please give 24 hours' notice to cancel or reschedule. Late cancellations are charged the full session fee."
    }
]

const ServicesHeroMedia: FC = (): ReactNode => {
    return (
        <div className='relative mx-auto w-full max-w-md pb-10 pl-10 sm:pb-14 sm:pl-14'>
            <div className='relative aspect-[4/5] overflow-hidden rounded-[2rem] shadow-2xl shadow-plum/15'>
                <Image
                    src='/images/services-office.png'
                    alt="A white armchair and a leafy plant against a soft lavender wall in a calm office"
                    fill
                    preload
                    sizes='(min-width: 1024px) 30vw, (min-width: 640px) 24rem, 90vw'
                    className='object-cover'
                />
            </div>

            <div className='absolute bottom-0 left-0 aspect-square w-[46%] overflow-hidden rounded-[1.5rem] border-[6px] border-shell shadow-xl shadow-plum/15'>
                <Image
                    src='/images/telehealth-laptop.jpg'
                    alt="A woman at home with a laptop, a notebook and a cup of coffee"
                    fill
                    sizes='(min-width: 1024px) 14vw, 45vw'
                    className='object-cover'
                />
            </div>
        </div>
    )
}

const Services: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Services'
                description="Individual therapy, an 8-week anxiety skills intensive and support for new moms in Olympia, WA and online across Washington. Clear pricing and a free 15-minute consult."
            />

            <PageHero
                eyebrow={<HeroPill>Services and pricing</HeroPill>}
                title="Therapy That Fits Your Busy Life"
                description={
                    <p>
                        In person in downtown Olympia or online anywhere in Washington. You'll know exactly what to expect, what it costs and how often we'll meet, so the only thing you have to do is show up.
                    </p>
                }
                actions={<BookingButton size='lg' />}
                media={<ServicesHeroMedia />}
            />

            <section id='services-list' aria-labelledby='services-list-heading' className='py-16 sm:py-24'>
                <h2 id='services-list-heading' className='sr-only'>
                    Services and pricing
                </h2>

                <div className='mx-auto grid max-w-7xl gap-8 px-5 sm:px-8 lg:grid-cols-2'>
                    {services.map((service) => {
                        const Icon = service.icon

                        return (
                            <article
                                key={service.id}
                                id={service.id}
                                className={cn(
                                    'reveal flex scroll-mt-28 flex-col rounded-[2rem] p-8 sm:p-10',
                                    service.featured ? 'bg-lavender' : 'border border-border bg-card'
                                )}
                            >
                                <div className='flex items-start justify-between gap-4'>
                                    <span className={cn('grid size-14 place-items-center rounded-full text-plum', service.featured ? 'bg-shell' : 'bg-lavender')}>
                                        <Icon className='size-6' aria-hidden='true' />
                                    </span>

                                    {service.featured && (
                                        <span className='rounded-full bg-honey px-3.5 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-espresso'>
                                            Start here
                                        </span>
                                    )}
                                </div>

                                <h3 className='mt-6 font-heading text-3xl leading-tight text-plum sm:text-[2.1rem]'>
                                    {service.name}
                                </h3>

                                <p className='mt-3 text-espresso'>
                                    <span className='font-semibold text-plum'>Who it's for:</span> {service.audience}
                                </p>

                                <p className='mt-4 text-lg leading-relaxed text-espresso-soft'>
                                    {service.description}
                                </p>

                                <h4 className='mt-7 text-sm font-semibold uppercase tracking-[0.16em] text-espresso'>
                                    What's included
                                </h4>

                                <ul className='mt-3 space-y-2.5'>
                                    {service.included.map((item) => (
                                        <li key={item} className='flex gap-3 text-espresso'>
                                            <Check className='mt-1 size-4 shrink-0 text-plum' aria-hidden='true' />
                                            {item}
                                        </li>
                                    ))}
                                </ul>

                                <div className='mt-auto pt-8'>
                                    <div className={cn('flex flex-wrap items-end justify-between gap-5 border-t pt-7', service.featured ? 'border-plum/15' : 'border-border')}>
                                        <div>
                                            <p className='text-sm font-medium text-espresso-soft'>
                                                Price
                                            </p>

                                            <p className='mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-1'>
                                                <span className='font-heading text-4xl leading-none text-plum'>
                                                    {service.price}
                                                </span>

                                                {service.priceNote && (
                                                    <span className='text-espresso-soft'>
                                                        {service.priceNote}
                                                    </span>
                                                )}

                                                {service.badge && (
                                                    <span className='rounded-full bg-honey/45 px-2.5 py-0.5 text-sm font-medium text-espresso'>
                                                        {service.badge}
                                                    </span>
                                                )}
                                            </p>
                                        </div>

                                        <BookingButton variant={service.featured ? 'primary' : 'outline'} />
                                    </div>
                                </div>
                            </article>
                        )
                    })}
                </div>
            </section>

            <section id='insurance-and-anxiety' className='pb-16 sm:pb-24'>
                <div className='mx-auto grid max-w-7xl gap-8 px-5 sm:px-8 lg:grid-cols-2'>
                    <div id='insurance' className='reveal flex scroll-mt-28 flex-col gap-5 rounded-[2rem] bg-sand p-8 sm:flex-row sm:p-10'>
                        <span className='grid size-14 shrink-0 place-items-center rounded-full bg-shell text-plum'>
                            <ReceiptText className='size-6' aria-hidden='true' />
                        </span>

                        <div>
                            <h2 className='font-heading text-3xl text-plum'>
                                Insurance
                            </h2>

                            <p className='mt-3 text-lg leading-relaxed text-espresso'>
                                I'm private pay and out of network. I can give you a superbill to submit to your insurance for possible reimbursement.
                            </p>
                        </div>
                    </div>

                    <div className='reveal relative flex flex-col overflow-hidden rounded-[2rem] bg-plum p-8 text-white sm:p-10'>
                        <h2 className='font-heading text-3xl leading-tight text-balance'>
                            Anxiety Therapy for Women in Olympia and Online
                        </h2>

                        <p className='mt-3 text-lg leading-relaxed text-white/85'>
                            For the racing thoughts, the constant what-ifs and the feeling that you can never fully relax.
                        </p>

                        <ButtonLink href='/anxiety-therapy-olympia' variant='honey' className='mt-7 self-start'>
                            Learn More
                            <span className='sr-only'> about anxiety therapy for women</span>
                            <ArrowRight className='size-4' aria-hidden='true' />
                        </ButtonLink>
                    </div>
                </div>
            </section>

            <section id='faq' className='scroll-mt-28 bg-lavender py-20 sm:py-28'>
                <div className='mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-12 lg:gap-16'>
                    <div className='reveal lg:col-span-4'>
                        <div className='lg:sticky lg:top-28'>
                            <SectionHeading
                                eyebrow="Questions"
                                title="Frequently Asked Questions"
                            />
                        </div>
                    </div>

                    <div className='reveal space-y-4 lg:col-span-8'>
                        {faqs.map((faq) => (
                            <details key={faq.question} className='group rounded-[1.5rem] bg-shell px-6 py-5 shadow-sm transition-colors sm:px-8 sm:py-6'>
                                <summary className='flex cursor-pointer list-none items-center justify-between gap-6 rounded-lg font-heading text-xl text-plum focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-plum sm:text-2xl [&::-webkit-details-marker]:hidden'>
                                    {faq.question}

                                    <span className='grid size-10 shrink-0 place-items-center rounded-full bg-lavender text-plum transition-transform duration-200 group-open:rotate-45'>
                                        <Plus className='size-5' aria-hidden='true' />
                                    </span>
                                </summary>

                                <p className='mt-4 max-w-3xl text-lg leading-relaxed text-espresso-soft'>
                                    {faq.answer}
                                </p>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            <CtaBand
                title="Find the Right Fit, No Pressure"
                description="If we're not the right match, I'll help you find a therapist who is."
                buttonLabel="Book a Free Consult"
            />
        </Layout>
    )
}

export default Services
